import { useState } from 'react'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { ConfirmDialog } from '@/components/Dialog'
import { ErrorNotice } from '@/components/ErrorNotice'
import { SkeletonRows } from '@/components/Skeleton'
import { useRevokeSession, useSessions } from '@/hooks/useAccount'
import { useNow } from '@/hooks/useNow'
import { describeUserAgent, relativeTime } from '@/lib/format'
import type { SessionSummary } from '@/lib/types'

export function SessionsCard() {
  const sessions = useSessions()
  const revoke = useRevokeSession()
  const now = useNow(60_000)
  const [pending, setPending] = useState<SessionSummary | null>(null)

  const items = sessions.data?.items ?? []

  return (
    <Card>
      <CardHeader
        title="Signed-in devices"
        description="Revoke anything you do not recognise. Sessions last 30 days."
      />
      <CardBody flush>
        {sessions.isPending ? (
          <div className="px-4 py-4 sm:px-5">
            <SkeletonRows rows={3} />
          </div>
        ) : sessions.isError ? (
          <div className="px-4 py-4 sm:px-5">
            <ErrorNotice error={sessions.error} onRetry={() => void sessions.refetch()} />
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-4 text-sm text-muted sm:px-5">No other sessions.</p>
        ) : (
          <ul>
            {items.map((session) => (
              <li
                key={session.id}
                className="board-row flex flex-wrap items-center gap-3 px-4 py-3 sm:px-5"
              >
                <div className="min-w-0 flex-1">
                  <p className="signage-tight flex items-center gap-2 text-sm text-ink">
                    {describeUserAgent(session.userAgent)}
                    {session.current ? <Badge tone="accent">This device</Badge> : null}
                  </p>
                  {/* Address and last-seen are both scanned column-wise down the
                      list, so they stay mono and keep their digits aligned. */}
                  <p className="mt-1 font-mono text-[11px] text-faint">
                    {session.ip ? `${session.ip} · ` : ''}
                    last seen {relativeTime(session.lastSeenAt ?? session.createdAt, now)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPending(session)}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return
          revoke.mutate(pending.id, { onSettled: () => setPending(null) })
        }}
        loading={revoke.isPending}
        destructive
        confirmLabel="Revoke session"
        title="Revoke this session?"
        description={
          pending?.current
            ? 'This is the device you are using — you will be signed out immediately.'
            : `${describeUserAgent(pending?.userAgent)} will have to sign in again.`
        }
      />
    </Card>
  )
}
